// Combobox — the ARIA 1.2 combobox with a listbox popup used by the row form's foreign-key
// field (pages/operate/ForeignKeyWidget.jsx). It owns the
// keyboard and the popup; the caller owns the text, the options and where they come from.
//
//   <Combobox
//     id="..."  label="station_id"            // the input's id and accessible name
//     text={text} onText={t => ...}            // the typed text (controlled)
//     options={[...]} optionKey={o => key}     // what the popup lists
//     renderOption={(o, {active}) => <...>}   // one option's content
//     onPick={o => ...}                        // Enter or click on an option
//     onFocus={() => ...}                      // optional; with openOnFocus the popup opens at once
//     openOnFocus={false} clearOnEscape={true} // Escape on a closed popup empties the text
//     status="12 rows found" statusError={false} busy={false}   // the polite live region
//     listLabel="Row suggestions" describedBy="hint-id" placeholder disabled required inputRef
//   />
//
// ArrowDown opens / moves, ArrowUp moves, Enter picks the active option, Escape closes (and,
// on a closed popup with clearOnEscape, empties the text). The active option is named by
// aria-activedescendant (`<id>-listbox-opt-<i>`); the popup closes when focus leaves it.
import { useEffect, useRef, useState } from "react";
import { G, G_BG, Input, Spinner } from "../components/Shared";

export default function Combobox({ id, label, text, onText, options, optionKey, renderOption, onPick,
                                   onFocus, openOnFocus = false, clearOnEscape = true, status = "",
                                   statusError = false, busy = false, listLabel, describedBy, placeholder,
                                   disabled = false, required, inputRef, listTop = 46 }) {
  const listId = `${id}-listbox`;
  const statusId = `${id}-status`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const ownRef = useRef(null);
  const ref = inputRef || ownRef;

  // new options: nothing is active until the arrow keys say so
  useEffect(() => { setActive(-1); }, [options]);

  const expanded = open && options.length > 0;

  const pick = o => {
    setOpen(false);
    setActive(-1);
    onPick(o);
  };

  const onKeyDown = e => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!expanded && options.length) { setOpen(true); setActive(0); return; }
      if (options.length) setActive(a => (a + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (options.length) setActive(a => (a <= 0 ? options.length - 1 : a - 1));
    } else if (e.key === "Enter") {
      if (expanded && active >= 0 && options[active]) {
        e.preventDefault();
        pick(options[active]);
      }
    } else if (e.key === "Escape") {
      if (expanded) { e.preventDefault(); setOpen(false); setActive(-1); }
      else if (clearOnEscape && text) { e.preventDefault(); onText(""); }
    }
  };

  return (
    <div style={{ position: "relative" }}
         // Shared <Input> owns its own onBlur (focus ring), so closing happens on the wrapper.
         onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
      {/* Shared <Input> sets its own onFocus too: the focus is caught on this wrapper */}
      <div style={{ position: "relative" }}
           onFocus={() => { if (openOnFocus) setOpen(true); if (onFocus) onFocus(); }}>
        <Input
          ref={ref}
          id={id}
          type="text"
          role="combobox"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-activedescendant={expanded && active >= 0 ? `${listId}-opt-${active}` : undefined}
          aria-describedby={[statusId, describedBy].filter(Boolean).join(" ")}
          aria-required={required || undefined}
          autoComplete="off"
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          onChange={e => { onText(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          style={{ paddingRight: 34 }}
        />
        {busy && <span style={{ position: "absolute", right: 12, top: 13 }} aria-hidden="true"><Spinner /></span>}
      </div>
      <div id={statusId} role="status" aria-live="polite"
           style={{ fontSize: 12, color: statusError ? "#b91c1c" : "#6b7280", minHeight: status ? 16 : 0,
                    marginTop: status ? 4 : 0 }}>
        {status}
      </div>
      <ul id={listId} role="listbox" aria-label={listLabel || `${label} suggestions`} hidden={!expanded}
          style={{ position: "absolute", zIndex: 20, left: 0, right: 0, top: listTop, margin: 0, padding: 4,
                   listStyle: "none", background: "#fff", border: "1px solid #d1d5db", borderRadius: 8,
                   boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 300, overflowY: "auto" }}>
        {expanded && options.map((o, i) => {
          const isActive = i === active;
          return (
            <li key={`${optionKey(o)}-${i}`} id={`${listId}-opt-${i}`} role="option" aria-selected={isActive}
                onMouseDown={e => e.preventDefault()}
                onClick={() => pick(o)}
                onMouseEnter={() => setActive(i)}
                style={{ padding: "7px 10px", borderRadius: 6, cursor: "pointer",
                         background: isActive ? G_BG : "transparent",
                         borderLeft: `3px solid ${isActive ? G : "transparent"}` }}>
              {renderOption(o, { active: isActive })}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
